'use client'

import type { ModelCatalogDiff, ModelProbeReport } from '@/lib/trpc'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { useTRPC } from '@/lib/trpc'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, CloudDownload, Eye, Loader2, RefreshCw, Save, XCircle, Zap } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

type ModelConfig = Record<string, string[]>
type GamePools = { lowCost: ModelConfig, highPerformance: ModelConfig }
type Candidate = ModelCatalogDiff['byAuthor'][string][number]
type CandidateSort = { field: 'input' | 'output' | 'context', dir: 'asc' | 'desc' }
type Notice = { type: 'success' | 'error' | 'info', text: string }

const EMPTY_CONFIG: ModelConfig = {}
const EMPTY_POOLS: GamePools = { lowCost: {}, highPerformance: {} }
const EMPTY_HIDDEN_MODELS: string[] = []
const DEFAULT_SORT: CandidateSort = { field: 'output', dir: 'desc' }
const PROVIDER_LABELS: Record<string, string> = {
  'openai': 'OpenAI',
  'google': 'Google',
  'anthropic': 'Anthropic',
  'x-ai': 'xAI',
  'deepseek': 'DeepSeek',
  'z-ai': 'Z-AI (GLM)',
  'moonshotai': 'Moonshot AI',
}

function errMsg(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function formatPerMillion(price: number | null | undefined): string {
  if (price === null || price === undefined)
    return '—'
  const value = price * 1_000_000
  if (value === 0)
    return '$0'
  return `$${value.toFixed(value < 0.01 ? 4 : 2)}`
}

function shortId(id: string): string {
  return id.split('/').slice(1).join('/') || id
}

function isCandidateBillable(candidate: Candidate): boolean {
  return candidate.billable
    ?? (candidate.pricing !== null
      && Number.isFinite(candidate.pricing.input)
      && Number.isFinite(candidate.pricing.output)
      && candidate.pricing.input >= 0
      && candidate.pricing.output >= 0
      && (candidate.pricing.input > 0 || candidate.pricing.output > 0))
}

function configKey(config: ModelConfig): string {
  return JSON.stringify(Object.entries(config)
    .map(([author, ids]) => [author, [...new Set(ids)].sort()] as const)
    .filter(([, ids]) => ids.length > 0)
    .sort(([left], [right]) => left.localeCompare(right)))
}

function unionConfig(pools: GamePools): ModelConfig {
  const config: ModelConfig = {}
  for (const pool of [pools.lowCost, pools.highPerformance]) {
    for (const [author, ids] of Object.entries(pool))
      config[author] = [...new Set([...(config[author] ?? []), ...ids])]
  }
  return config
}

function countModels(config: ModelConfig): number {
  return Object.values(config).flat().length
}

function ProbeResult({ report }: { report?: ModelProbeReport }) {
  if (!report)
    return null
  return (
    <span
      title={report.error ?? `${report.latencyMs}ms`}
      className={`inline-flex items-center gap-1 text-xs ${report.ok ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}
    >
      {report.ok ? <CheckCircle2 className="size-3.5 shrink-0" aria-hidden="true" /> : <XCircle className="size-3.5 shrink-0" aria-hidden="true" />}
      {report.ok ? `${report.latencyMs}ms` : 'Failed'}
    </span>
  )
}

export function ModelCatalogSection() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const catalogQuery = useQuery(trpc.queryRouter.getLastCatalogDiff.queryOptions())
  const poolsQuery = useQuery(trpc.queryRouter.getGameModelPools.queryOptions())
  const configQuery = useQuery(trpc.queryRouter.getModelConfig.queryOptions())
  const hiddenModelsQuery = useQuery(trpc.queryRouter.getHiddenCatalogModels.queryOptions())
  const syncMutation = useMutation(trpc.queryRouter.fetchModelCatalogDiff.mutationOptions())
  const applyMutation = useMutation(trpc.queryRouter.applyModelConfigUpdate.mutationOptions())
  const redisMutation = useMutation(trpc.queryRouter.refreshResponsiveModelsCache.mutationOptions())
  const hideMutation = useMutation(trpc.queryRouter.hideCatalogModel.mutationOptions())
  const unhideMutation = useMutation(trpc.queryRouter.unhideCatalogModels.mutationOptions())
  const [draftPools, setDraftPools] = useState<GamePools | null>(null)
  const [probes, setProbes] = useState<Record<string, ModelProbeReport>>({})
  const [message, setMessage] = useState<Notice | null>(null)
  const [confirmApply, setConfirmApply] = useState(false)
  const [sortBy, setSortBy] = useState<Record<string, CandidateSort>>({})
  const actionsRef = useRef<HTMLDivElement>(null)
  const catalogRowsRef = useRef<HTMLDivElement>(null)
  const visibilityFocus = useRef<'hide' | 'unhide' | null>(null)

  const diff = catalogQuery.data?.data
  const hiddenModels = hiddenModelsQuery.data?.data ?? EMPTY_HIDDEN_MODELS
  const hiddenSet = new Set(hiddenModels)
  const savedPools = poolsQuery.data?.data ?? EMPTY_POOLS
  const savedConfig = configQuery.data?.data ?? EMPTY_CONFIG
  const pools = draftPools ?? savedPools
  const modelConfig = unionConfig(pools)
  const selectedIds = Object.values(modelConfig).flat()
  const savedIds = Object.values(savedConfig).flat()
  const selectedSet = new Set(selectedIds)
  const savedSet = new Set(savedIds)
  const added = selectedIds.filter(id => !savedSet.has(id))
  const removed = savedIds.filter(id => !selectedSet.has(id))
  const lowCount = countModels(pools.lowCost)
  const highCount = countModels(pools.highPerformance)
  const dirty = configKey(modelConfig) !== configKey(savedConfig)
    || configKey(pools.lowCost) !== configKey(savedPools.lowCost)
    || configKey(pools.highPerformance) !== configKey(savedPools.highPerformance)
  const loading = catalogQuery.isPending || poolsQuery.isPending || configQuery.isPending || hiddenModelsQuery.isPending
  const loadError = catalogQuery.error ?? poolsQuery.error ?? configQuery.error ?? hiddenModelsQuery.error
  const busy = syncMutation.isPending || applyMutation.isPending || redisMutation.isPending
    || hideMutation.isPending || unhideMutation.isPending

  useEffect(() => {
    if (hideMutation.isPending || unhideMutation.isPending)
      return
    if (visibilityFocus.current === 'hide')
      actionsRef.current?.querySelector<HTMLButtonElement>('[data-action="unhide"]')?.focus({ preventScroll: true })
    else if (visibilityFocus.current === 'unhide')
      catalogRowsRef.current?.querySelector<HTMLButtonElement>('[data-hide-model]')?.focus({ preventScroll: true })
    visibilityFocus.current = null
  }, [hiddenModels, hideMutation.isPending, unhideMutation.isPending])

  const handleHide = async (model: string) => {
    setMessage(null)
    try {
      const result = await hideMutation.mutateAsync({ model })
      visibilityFocus.current = 'hide'
      queryClient.setQueryData(trpc.queryRouter.getHiddenCatalogModels.queryKey(), result)
      setMessage({ type: 'info', text: `${shortId(model)} hidden. Use Unhide to show all hidden models.` })
    }
    catch (error) {
      setMessage({ type: 'error', text: `Hide failed: ${errMsg(error)}` })
    }
  }

  const handleUnhide = async () => {
    setMessage(null)
    try {
      const result = await unhideMutation.mutateAsync()
      visibilityFocus.current = 'unhide'
      queryClient.setQueryData(trpc.queryRouter.getHiddenCatalogModels.queryKey(), result)
      setMessage({ type: 'info', text: 'All hidden models are visible again.' })
    }
    catch (error) {
      setMessage({ type: 'error', text: `Unhide failed: ${errMsg(error)}` })
    }
  }

  const togglePool = (kind: keyof GamePools, author: string, id: string, checked: boolean) => {
    setDraftPools((previous) => {
      const current = previous ?? savedPools
      const ids = new Set(current[kind][author] ?? [])
      if (checked)
        ids.add(id)
      else
        ids.delete(id)
      return { ...current, [kind]: { ...current[kind], [author]: [...ids] } }
    })
    setMessage(null)
    setProbes({})
  }

  const handleSync = async () => {
    setMessage(null)
    setProbes({})
    try {
      // Capture the draft before refreshing metadata; Resync never resets pool choices.
      setDraftPools(current => current ?? pools)
      const result = await syncMutation.mutateAsync()
      queryClient.setQueryData(trpc.queryRouter.getLastCatalogDiff.queryKey(), result)
      setMessage({ type: 'info', text: `${result.message}. Your pool selections were kept.` })
    }
    catch (error) {
      setMessage({ type: 'error', text: `Resync failed: ${errMsg(error)}` })
    }
  }

  const handleApply = async () => {
    setMessage(null)
    try {
      const result = await applyMutation.mutateAsync(pools)
      queryClient.setQueryData(trpc.queryRouter.getGameModelPools.queryKey(), {
        success: true,
        data: { lowCost: result.data.lowCost, highPerformance: result.data.highPerformance },
        message: result.message,
      })
      queryClient.setQueryData(trpc.queryRouter.getModelConfig.queryKey(), {
        success: true,
        data: result.data.modelConfig,
        message: result.message,
      })
      setDraftPools(null)
      setProbes({})
      setConfirmApply(false)
      setMessage({ type: 'success', text: `${result.message}. Use Update Redis to check responses and update the live game pools.` })
    }
    catch (error) {
      setConfirmApply(false)
      setMessage({ type: 'error', text: `Apply failed: ${errMsg(error)}` })
    }
  }

  const handleUpdateRedis = async () => {
    if (dirty) {
      setMessage({ type: 'info', text: 'Apply Model first to save your pool selections.' })
      return
    }
    setMessage(null)
    setProbes({})
    try {
      const result = await redisMutation.mutateAsync()
      setProbes(Object.fromEntries(result.data.reports.map(report => [report.model, report])))
      const failures = result.data.reports.filter(report => !report.ok).length
      const excluded = result.data.excludedUnpriced.length
      setMessage({
        type: result.success ? 'success' : 'error',
        text: result.success
          ? `Redis updated: ${result.data.lowCost.length} low-cost / ${result.data.highPerformance.length} high-performance models. ${failures} failed; ${excluded} excluded without usable pricing.`
          : `${result.message} Redis was not changed.`,
      })
    }
    catch (error) {
      setMessage({ type: 'error', text: `Update Redis failed: ${errMsg(error)}` })
    }
  }

  const toggleSort = (author: string, field: CandidateSort['field']) => {
    setSortBy((previous) => {
      const current = previous[author] ?? DEFAULT_SORT
      return { ...previous, [author]: { field, dir: current.field === field && current.dir === 'desc' ? 'asc' : 'desc' } }
    })
  }

  const candidatesByAuthor = Object.fromEntries(Object.entries(diff?.byAuthor ?? {})
    .map(([author, candidates]) => [author, [...candidates]]))
  // Keep draft members removable if Resync no longer returns their catalog row.
  for (const [author, ids] of Object.entries(modelConfig)) {
    const candidates = candidatesByAuthor[author] ?? []
    candidatesByAuthor[author] = candidates
    for (const id of ids) {
      if (candidates.some(candidate => candidate.id === id))
        continue
      candidates.push({
        id,
        author,
        name: id,
        created: null,
        contextLength: null,
        pricing: null,
        inputModalities: [],
        outputModalities: [],
        isDated: false,
        isExpiring: false,
        inCurrentConfig: savedSet.has(id),
        suggested: false,
        missingFromOpenRouter: true,
        billable: false,
      })
    }
  }
  const authorsInOrder = [...new Set([...(diff?.authors ?? []), ...Object.keys(candidatesByAuthor)])]
    .filter(author => candidatesByAuthor[author]?.some(candidate => !hiddenSet.has(candidate.id)))
  const failedReports = Object.values(probes).filter(report => !report.ok && !hiddenSet.has(report.model))

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[100rem]">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">Model Catalog, Costs &amp; Game Pools</h2>
          <p className="mx-auto max-w-3xl text-sm text-muted-foreground">
            Resync the model catalog and prices from OpenRouter. Select Low-Cost or High-Perf to include a model,
            then Apply Model to save your configuration. Update Redis checks the saved models and publishes responsive models for games.
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <CloudDownload className="size-5" aria-hidden="true" />
              Model Catalog
              {diff && <span className="text-xs font-normal text-muted-foreground">{diff.totalCatalogSize} models in source · synced {new Date(diff.fetchedAt).toLocaleString()}</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5" aria-busy={loading || busy}>
            {loading && <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" aria-hidden="true" />Loading catalog and saved pools...</p>}
            {loadError && <p role="alert" className="text-sm text-red-700 dark:text-red-400">Could not load the saved catalog or configuration: {errMsg(loadError)}. Reload the page to try again.</p>}
            {!loading && !loadError && !diff && <p className="py-6 text-center text-sm text-muted-foreground">No catalog loaded. Use Resync below to fetch the model list and prices.</p>}
            {diff && (
              <div ref={catalogRowsRef} className="space-y-6">
                {authorsInOrder.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{hiddenModels.length > 0 ? 'No visible models. Use Unhide below to show all hidden models.' : 'No game-compatible models found. Use Resync to refresh the catalog.'}</p>}
                {authorsInOrder.map((author) => {
                  const sort = sortBy[author] ?? DEFAULT_SORT
                  const candidates = (candidatesByAuthor[author] ?? []).filter(candidate => !hiddenSet.has(candidate.id)).sort((left, right) => {
                    const value = (candidate: Candidate) => (sort.field === 'context' ? candidate.contextLength : candidate.pricing?.[sort.field]) ?? Number.NEGATIVE_INFINITY
                    const leftValue = value(left)
                    const rightValue = value(right)
                    return leftValue === rightValue ? left.id.localeCompare(right.id) : sort.dir === 'desc' ? rightValue - leftValue : leftValue - rightValue
                  })
                  return (
                    <div key={author}>
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold">{PROVIDER_LABELS[author] ?? author}</h3>
                        <div className="flex items-center gap-1" aria-label={`Sort ${PROVIDER_LABELS[author] ?? author} models`}>
                          {(['context', 'input', 'output'] as const).map(field => (
                            <Button key={field} variant="ghost" size="sm" onClick={() => toggleSort(author, field)} aria-pressed={sort.field === field} className="h-8 px-2 text-xs">
                              {field === 'context' ? 'Ctx' : field === 'input' ? 'In $' : 'Out $'}
                              {sort.field === field ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full min-w-[620px] text-sm">
                          <thead className="bg-muted/50 text-xs text-muted-foreground">
                            <tr>
                              <th scope="col" className="px-3 py-2 text-left font-medium">Model</th>
                              <th scope="col" className="px-3 py-2 text-center font-medium">Low-Cost</th>
                              <th scope="col" className="px-3 py-2 text-center font-medium">High-Perf</th>
                              <th scope="col" className="px-3 py-2 text-right font-medium">Ctx · In/Out $ per 1M</th>
                              <th scope="col" className="px-3 py-2 text-right font-medium">Response</th>
                              <th scope="col" className="relative px-3 py-2 text-right font-medium"><span className="sr-only">Visibility</span></th>
                            </tr>
                          </thead>
                          <tbody>
                            {candidates.map((candidate) => {
                              const billable = isCandidateBillable(candidate)
                              return (
                                <tr key={candidate.id} className="border-t border-border hover:bg-muted/30">
                                  <th scope="row" className="px-3 py-3 text-left font-normal">
                                    <span className="font-mono text-xs text-foreground">{shortId(candidate.id)}</span>
                                    {!billable && <span className="mt-1 block text-xs text-red-700 dark:text-red-400">Usable pricing required. Resync or remove this model from its pools.</span>}
                                  </th>
                                  {(['lowCost', 'highPerformance'] as const).map((kind) => {
                                    const checked = pools[kind][author]?.includes(candidate.id) ?? false
                                    return (
                                      <td key={kind} className="px-3 py-3 text-center">
                                        <Checkbox
                                          checked={checked}
                                          disabled={loading || busy || !!loadError || (!billable && !checked)}
                                          onCheckedChange={value => togglePool(kind, author, candidate.id, value === true)}
                                          aria-label={`${kind === 'lowCost' ? 'Low-cost' : 'High-performance'} pool: ${shortId(candidate.id)}`}
                                          title={billable ? undefined : 'A usable price is required before adding this model'}
                                        />
                                      </td>
                                    )
                                  })}
                                  <td className="px-3 py-3 text-right text-xs tabular-nums text-muted-foreground">
                                    <span>{candidate.contextLength ? `${Math.round(candidate.contextLength / 1000)}k · ` : ''}</span>
                                    <span className="font-mono">{formatPerMillion(candidate.pricing?.input)}/{formatPerMillion(candidate.pricing?.output)}</span>
                                  </td>
                                  <td className="px-3 py-3 text-right"><ProbeResult report={probes[candidate.id]} /></td>
                                  <td className="px-3 py-3 text-right">
                                    <Button
                                      data-hide-model={candidate.id}
                                      variant="ghost"
                                      size="sm"
                                      disabled={loading || busy || !!loadError}
                                      onClick={() => { void handleHide(candidate.id) }}
                                      aria-label={`Hide ${shortId(candidate.id)}`}
                                    >
                                      {hideMutation.isPending && hideMutation.variables?.model === candidate.id ? 'Hiding...' : 'Hide'}
                                    </Button>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            <div className="space-y-3 border-t border-border pt-4">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span>Low-Cost <strong>{lowCount}</strong> · High-Perf <strong>{highCount}</strong> · Config <strong>{selectedIds.length}</strong></span>
                {hiddenModels.length > 0 && <span className="text-muted-foreground">Hidden {hiddenModels.length}</span>}
                {dirty && !loading && <span className="text-muted-foreground">Unsaved changes — Apply Model before Update Redis.</span>}
              </div>
              {removed.length > 0 && !loading && <p className="text-xs text-muted-foreground">Apply Model will remove {removed.length} configured model(s) that are not selected in either pool. Review the list before confirming.</p>}
              <div ref={actionsRef} className="flex flex-wrap gap-3">
                <Button onClick={() => { void handleSync() }} disabled={loading || busy} variant="outline" className="gap-2">
                  {syncMutation.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="size-4" aria-hidden="true" />}
                  {syncMutation.isPending ? 'Resyncing...' : 'Resync'}
                </Button>
                <Button data-action="apply-model" onClick={() => setConfirmApply(true)} disabled={loading || busy || !!loadError || !diff || selectedIds.length === 0 || !dirty} className="gap-2">
                  {applyMutation.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Save className="size-4" aria-hidden="true" />}
                  {applyMutation.isPending ? 'Applying...' : 'Apply Model'}
                </Button>
                <Button data-action="update-redis" onClick={() => { void handleUpdateRedis() }} disabled={loading || busy || !!loadError || dirty || selectedIds.length === 0} variant="outline" className="gap-2" title={dirty ? 'Apply Model first to save your selections' : undefined}>
                  {redisMutation.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Zap className="size-4" aria-hidden="true" />}
                  {redisMutation.isPending ? 'Checking models...' : 'Update Redis'}
                </Button>
                <Button data-action="unhide" onClick={() => { void handleUnhide() }} disabled={loading || busy || !!loadError || hiddenModels.length === 0} variant="outline" className="gap-2" title="Show all hidden models">
                  {unhideMutation.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                  {unhideMutation.isPending ? 'Unhiding...' : 'Unhide'}
                </Button>
              </div>
              {message && <p role={message.type === 'error' ? 'alert' : 'status'} className={`rounded-md px-3 py-2 text-sm ${message.type === 'error' ? 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200' : message.type === 'success' ? 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200' : 'bg-muted text-foreground'}`}>{message.text}</p>}
              {failedReports.length > 0 && <ul className="space-y-1 text-xs text-red-700 dark:text-red-400">{failedReports.map(report => <li key={report.model}><span className="font-mono">{report.model}</span>: {report.error ?? 'No response'}</li>)}</ul>}
            </div>
          </CardContent>
        </Card>
      </div>
      <AlertDialog.Root open={confirmApply} onOpenChange={open => !applyMutation.isPending && setConfirmApply(open)}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
          <AlertDialog.Content
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              actionsRef.current?.querySelector<HTMLButtonElement>(
                'button[data-action]:not(:disabled)',
              )?.focus()
            }}
            className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border bg-background p-6 shadow-lg"
          >
            <AlertDialog.Title className="mb-3 text-lg font-semibold">Apply Model?</AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-muted-foreground">Save {selectedIds.length} unique models in model_config, with {lowCount} low-cost and {highCount} high-performance selections. Redis is updated separately with Update Redis.</AlertDialog.Description>
            {(!lowCount || !highCount) && <p className="mt-3 flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300"><AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />One pool is empty. Games using that tier may fall back to the other pool.</p>}
            {(added.length > 0 || removed.length > 0) && <div className="mt-4 space-y-3 text-sm">{added.length > 0 && <div><p className="font-medium">Add to config ({added.length})</p><p className="mt-1 break-words font-mono text-xs text-muted-foreground">{added.join(', ')}</p></div>}{removed.length > 0 && <div><p className="font-medium">Remove from config ({removed.length})</p><p className="mt-1 break-words font-mono text-xs text-muted-foreground">{removed.join(', ')}</p></div>}</div>}
            <div className="mt-6 flex justify-end gap-3">
              <AlertDialog.Cancel asChild><Button variant="outline" disabled={applyMutation.isPending}>Cancel</Button></AlertDialog.Cancel>
              <Button onClick={() => { void handleApply() }} disabled={applyMutation.isPending} className="gap-2">{applyMutation.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}Confirm apply</Button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  )
}
